import type { SessionState, GuidanceTemplate, ComputedMetrics } from '@/state/types.ts';

// Rule evaluation result
interface RuleMatch {
  ruleKey: string;
  priority: number;
  tipText: string;
}

// Evaluate all guidance rules and return matching tips
export function selectGuidanceTips(
  state: SessionState,
  templates: GuidanceTemplate[]
): string[] {
  const matches: RuleMatch[] = [];
  const metrics = state.computedMetrics;
  const context = state.context;
  
  if (!metrics || !context) {
    return ['Experiment with different genres to discover what works best for you.'];
  }

  // Evaluate each template
  templates.forEach((template) => {
    if (evaluateRule(template.ruleKey, state, metrics, context)) {
      matches.push({
        ruleKey: template.ruleKey,
        priority: template.priority,
        tipText: template.tipText,
      });
    }
  });

  // Sort by priority (ascending - lower number = higher priority)
  matches.sort((a, b) => a.priority - b.priority);

  // Take top 3 (or 2 if only 2 match)
  const selectedTips = matches.slice(0, 3).map((m) => m.tipText);

  // If fewer than 2 matches, add generic fallback
  if (selectedTips.length < 2) {
    selectedTips.push('Experiment with different genres to discover what works best for you.');
  }

  return selectedTips;
}

// Evaluate a single rule
function evaluateRule(
  ruleKey: string,
  state: SessionState,
  metrics: ComputedMetrics,
  context: { activity: string; energy: number; stress: number }
): boolean {
  const { reactionStrength, residualEffect, shiftEffect } = metrics;
  const { shiftChoice } = state;
  const { stress } = context;

  switch (ruleKey) {
    case 'high_reaction_strength':
      return reactionStrength >= 2.0;

    case 'low_reaction_strength':
      return reactionStrength < 1.0;

    case 'high_residual_positive':
      return residualEffect > 1.0;

    case 'high_residual_negative':
      return residualEffect < -1.0;

    case 'shift_effective':
      return shiftChoice === 'shift' && shiftEffect < -0.5;

    case 'shift_ineffective':
      return shiftChoice === 'shift' && shiftEffect >= -0.5;

    case 'stay_effective':
      return shiftChoice === 'stay';

    case 'high_stress_user':
      return stress >= 7;

    default:
      return false;
  }
}

// Default guidance templates (fallback if DB is empty)
export const defaultGuidanceTemplates: GuidanceTemplate[] = [
  {
    id: '1',
    ruleKey: 'high_reaction_strength',
    tipText: 'Music affects your mood strongly. Be mindful about what you listen to before important tasks.',
    priority: 1,
  },
  {
    id: '2',
    ruleKey: 'low_reaction_strength',
    tipText: 'Music has a subtle effect on you. Try longer listening sessions for a bigger impact.',
    priority: 2,
  },
  {
    id: '3',
    ruleKey: 'high_residual_positive',
    tipText: 'Positive feelings from music stay with you. Use uplifting tracks to start your day.',
    priority: 3,
  },
  {
    id: '4',
    ruleKey: 'high_residual_negative',
    tipText: 'Heavy music lingers. Use calm tracks after intense music to reset.',
    priority: 4,
  },
  {
    id: '5',
    ruleKey: 'shift_effective',
    tipText: 'Intentional mood shifting works for you. Try short transition playlists when you need a change.',
    priority: 5,
  },
  {
    id: '6',
    ruleKey: 'shift_ineffective',
    tipText: 'Mood shifts take time. Try gradual transitions: start with music matching your current mood, then slowly change.',
    priority: 6,
  },
  {
    id: '7',
    ruleKey: 'stay_effective',
    tipText: 'Sitting with your feelings is valid. Use music to deepen awareness, not just to change state.',
    priority: 7,
  },
  {
    id: '8',
    ruleKey: 'high_stress_user',
    tipText: 'Since your stress is high, avoid tense music before work. Try ambient or nature sounds.',
    priority: 8,
  },
];